import { AxiosError, AxiosResponse } from 'axios';
import {
  CorporateCharter,
  CorporateCharterRequest,
  FileResponse,
  OrderRequestPayload,
  RideWithScheduleItem,
  ScheduleItem,
  StringOrNumber,
} from 'utils/data/data-types';
import {
  ChangeRideEndSchemaType,
  RideOffersSchemaType,
} from 'components/forms/RideFormFactory/types';
import {
  DriverDtoSchema,
  DriverWithStatusDtoSchemaType,
  OrderDetailsSchemaPayloadType,
  RideDtoSchemaType,
  VehicleDtoSchema,
  VehicleWithStatusDtoSchema,
} from 'components/forms/BookingFormFactory/types';
import {
  ExportCSVQueryParams,
  OfferPriceRequest,
  OperatorsAlertsPaginationQueryParams,
  OperatorsDriversPaginationQueryParams,
  OperatorsInvoicesPaginationQueryParams,
  OperatorsListPaginationQueryParams,
  OperatorsUserMessagingMessagesQueryParams,
  OperatorsUserMessagingUsersQueryParams,
  OperatorsVehiclesLocationsQueryParams,
  OperatorsVehiclesPaginationQueryParams,
  PaginationQueryParams,
  RideMoveRequest,
  STALE_FIVE_MINUTES,
  STALE_ONE_HOUR,
  ScheduleQueryParams,
  VehicleActivationDto,
} from 'api/types';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  deleteOperatorsBankAccount,
  deleteOperatorsById,
  deleteOperatorsDriversById,
  deleteOperatorsFilesByFileName,
  deleteOperatorsOrdersByIdentifier,
  deleteOperatorsPricingSeasonalAdjustments,
  deleteOperatorsRidesLegsByIndex,
  deleteOperatorsUserMessagingGroupsMembersById,
  deleteOperatorsVehiclesById,
  deleteOperatorsVehiclesTemporaryBase,
  deleteScheduleById,
  fetchOperatorsAlerts,
  fetchOperatorsAlertsCountNew,
  fetchOperatorsBankAccount,
  fetchOperatorsById,
  fetchOperatorsDrivers,
  fetchOperatorsDriversById,
  fetchOperatorsDriversCandidates,
  fetchOperatorsDriversFiles,
  fetchOperatorsFileDownloadUrl,
  fetchOperatorsFiles,
  fetchOperatorsInvoices,
  fetchOperatorsOrdersById,
  fetchOperatorsOrdersPricingDetails,
  fetchOperatorsRidesPricingDetails,
  fetchOperatorsPricingRules,
  fetchOperatorsPricingSeasonalAdjustments,
  fetchOperatorsRidesActive,
  fetchOperatorsRidesFilter,
  fetchOperatorsRidesClientsExportCSV,
  fetchOperatorsRidesDriverCandidates,
  fetchOperatorsRidesVehicleCandidates,
  fetchOperatorsScheduleDrivers,
  fetchOperatorsScheduleDriversVacationLog,
  fetchOperatorsScheduleDriversWork,
  fetchOperatorsScheduleVehicles,
  fetchOperatorsScheduleVehiclesRepairLog,
  fetchOperatorsUserMessagingGroups,
  fetchOperatorsUserMessagingGroupsMembers,
  fetchOperatorsUserMessagingMessages,
  fetchOperatorsUserMessagingUsers,
  fetchOperatorsUsers,
  fetchOperatorsVehicleClasses,
  fetchOperatorsVehicleSizes,
  fetchOperatorsVehicles,
  fetchOperatorsVehiclesAll,
  fetchOperatorsVehiclesById,
  fetchOperatorsVehiclesFiles,
  fetchOperatorsVehiclesLocations,
  fetchOperatorsVehiclesTemporaryBase,
  postOperatorOrders,
  postOperatorsAlertsStatus,
  postOperatorsCalculator,
  postOperatorsCorporateCharters,
  postOperatorsDrivers,
  postOperatorsDriversInvitation,
  postOperatorsFiles,
  postOperatorsInvitation,
  postOperatorsOffers,
  postOperatorsOffersEmail,
  postOperatorsOffersPrice,
  postOperatorsPricingSeasonalAdjustments,
  postOperatorsRidesDriversMove,
  postOperatorsRidesFinish,
  postOperatorsRidesLegsByIndex,
  postOperatorsRidesMove,
  postOperatorsSchedule,
  postOperatorsUserMessagingGroups,
  postOperatorsUserMessagingGroupsArchive,
  postOperatorsUserMessagingGroupsMembers,
  postOperatorsUserMessagingMessages,
  postOperatorsUsersRoles,
  postOperatorsVehicles,
  postOperatorsVehiclesActivation,
  postOperatorsVehiclesDriversById,
  postOperatorsVehiclesFilesPhotosRank,
  postOperatorsVehiclesTemporaryBase,
  putOperatorsBankAccount,
  putOperatorsById,
  putOperatorsDrivers,
  patchOperatorsOrdersDetails,
  putOperatorsPricingRules,
  putOperatorsPricingSeasonalAdjustments,
  putOperatorsSchedule,
  putOperatorsUserMessagingGroupsById,
  putOperatorsVehicles,
  putOperatorsVehiclesTemporaryBase,
  fetchOperatorsOperatorTokenByAccessToken,
  fetchOperatorOffersByOperatorId,
  fetchOperatorsPlansActive,
  fetchOperatorsMileageRevenueSummary,
  fetchOperatorsClients,
  postOperatorsClients,
  putOperatorsClient,
  fetchOperatorsClientsAll,
} from 'api/requests/requests';
import {
  BusStatus,
  FileType,
  LegSchemaType,
  OfferSchemaType,
  SeenStatus,
  UserRole,
  OrderDtoSchemaType,
  UserPermission,
  ClientSchemaType,
} from 'remote/global';
import {
  OperatorDtoSchemaType,
  ScheduleItemSchemaType,
  SendOfferToClientRequestSchemaType,
  VehicleTemporaryBaseDtoSchemaType,
} from 'components/forms/types';
import {
  CalculatorResponseDtoSchemaType,
  CarrierPricingCostsSchemaType,
  PricingCalculatorSchemaType,
  PricingPeriodSchemaType,
  PricingRulesDtoSchemaType,
} from 'components/forms/CarrierFormFactory/types';
import {
  AccountUserSchemaType,
  OperatorBankAccountDtoSchemaType,
} from 'components/forms/AccountFactory/types';
import { triggerDownloadLink } from 'utils/helpers';
import {
  MessageGroupSchemaType,
  NewMessageGroupSchemaType,
  UserMessageDtoSchemaType,
} from 'components/forms/MessagingFactory/types';
import { useOperatorId, useUsersCurrentAccessesQuery } from './users';

export const operatorsQueryKeys = {
  all: ['operators'] as const,
  detail: (operatorId: StringOrNumber) => [...operatorsQueryKeys.all, operatorId] as const,
  offers: (operatorId: StringOrNumber, params: PaginationQueryParams) =>
    [...operatorsQueryKeys.all, operatorId, 'operator-offers', params] as const,
  users: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'users'] as const,
  usersWithParams: (operatorId: StringOrNumber, params: OperatorsListPaginationQueryParams) =>
    [...operatorsQueryKeys.detail(operatorId), 'users', params] as const,
  user: (operatorId: StringOrNumber, userId: StringOrNumber) =>
    [...operatorsQueryKeys.users(operatorId), userId] as const,
  userRoles: (operatorId: StringOrNumber, userId: StringOrNumber) =>
    [...operatorsQueryKeys.user(operatorId, userId), 'roles'] as const,
  schedule: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'schedule'] as const,
  scheduleVehicles: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.schedule(operatorId), 'vehicles'] as const,
  scheduleVehicle: (operatorId: StringOrNumber, vehicleId: string) =>
    [...operatorsQueryKeys.schedule(operatorId), 'vehicles', vehicleId] as const,
  scheduleVehiclesWithParams: (operatorId: StringOrNumber, params: ScheduleQueryParams) =>
    [...operatorsQueryKeys.scheduleVehicles(operatorId), 'filter', params] as const,
  scheduleVehiclesRepairLog: (operatorId: StringOrNumber, vehicleId: string) =>
    [...operatorsQueryKeys.scheduleVehicle(operatorId, vehicleId), 'repair-log'] as const,
  scheduleDriver: (operatorId: StringOrNumber, driverId: string) =>
    [...operatorsQueryKeys.schedule(operatorId), 'drivers', driverId] as const,
  scheduleDrivers: (operatorId: StringOrNumber, params: ScheduleQueryParams) =>
    [...operatorsQueryKeys.schedule(operatorId), 'drivers', 'filter', params] as const,
  scheduleDriversWork: (operatorId: StringOrNumber, params: ScheduleQueryParams) =>
    [...operatorsQueryKeys.schedule(operatorId), 'drivers', 'work', params] as const,
  scheduleDriversVacationLog: (
    operatorId: StringOrNumber,
    driverId: string,
    params: PaginationQueryParams
  ) =>
    [...operatorsQueryKeys.scheduleDriver(operatorId, driverId), 'vacation-log', params] as const,
  orders: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'orders'] as const,
  order: (operatorId: StringOrNumber, identifier: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'orders', identifier] as const,
  operatorsFiles: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'operator', 'files'] as const,
  invoices: (operatorId: StringOrNumber, params: OperatorsInvoicesPaginationQueryParams) =>
    [...operatorsQueryKeys.detail(operatorId), 'invoices', params] as const,
  pricingDetails: (operatorId: StringOrNumber, bookingNumber: StringOrNumber) =>
    [...operatorsQueryKeys.order(operatorId, bookingNumber), 'pricing-details'] as const,
  rides: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'rides'] as const,
  ride: (operatorId: StringOrNumber, rideId: string) =>
    [...operatorsQueryKeys.rides(operatorId), rideId] as const,
  rideDrivers: (operatorId: StringOrNumber, rideId: string) =>
    [...operatorsQueryKeys.ride(operatorId, rideId), 'drivers'] as const,
  rideMove: (operatorId: StringOrNumber, rideId: string) =>
    [...operatorsQueryKeys.ride(operatorId, rideId), 'move'] as const,
  ridesActive: (operatorId: StringOrNumber, params: OperatorsInvoicesPaginationQueryParams) =>
    [...operatorsQueryKeys.rides(operatorId), 'active', params] as const,
  ridesFilter: (operatorId: StringOrNumber, params: OperatorsInvoicesPaginationQueryParams) =>
    [...operatorsQueryKeys.rides(operatorId), 'active', params] as const,
  ridesFinish: (operatorId: StringOrNumber, rideId: string) =>
    [...operatorsQueryKeys.ride(operatorId, rideId), 'finish'] as const,
  vehicles: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'vehicles'] as const,
  vehiclesActivation: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.vehicles(operatorId), 'activation'] as const,
  vehicle: (operatorId: StringOrNumber, vehicleId: string) =>
    [...operatorsQueryKeys.vehicles(operatorId), vehicleId] as const,
  vehiclesAll: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.vehicles(operatorId), 'all'] as const,
  vehiclesFilter: (operatorId: StringOrNumber, params: OperatorsVehiclesPaginationQueryParams) =>
    [...operatorsQueryKeys.vehicles(operatorId), 'filter', params] as const,
  vehiclesLocationsFilter: (
    operatorId: StringOrNumber,
    params: OperatorsVehiclesLocationsQueryParams
  ) => [...operatorsQueryKeys.vehicles(operatorId), 'locations', 'filter', params] as const,
  vehiclesFiles: (operatorId: StringOrNumber, vehicleId: string) =>
    [...operatorsQueryKeys.vehicle(operatorId, vehicleId), 'files'] as const,
  vehiclesTemporaryBase: (operatorId: StringOrNumber, vehicleId: string) =>
    [...operatorsQueryKeys.vehicle(operatorId, vehicleId), 'temporary-base'] as const,
  vehiclesTemporaryBaseWithParams: (
    operatorId: StringOrNumber,
    vehicleId: string,
    params: PaginationQueryParams
  ) => [...operatorsQueryKeys.vehiclesTemporaryBase(operatorId, vehicleId), params] as const,
  vehiclesFilesPhotosRank: (operatorId: StringOrNumber, vehicleId: string) =>
    [...operatorsQueryKeys.vehiclesFiles(operatorId, vehicleId), 'photos', 'rank'] as const,
  drivers: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'drivers'] as const,
  driver: (operatorId: StringOrNumber, driverId: string) =>
    [...operatorsQueryKeys.drivers(operatorId), driverId] as const,
  driversFilter: (operatorId: StringOrNumber, params: OperatorsDriversPaginationQueryParams) =>
    [...operatorsQueryKeys.drivers(operatorId), 'filter', params] as const,
  driversInvitation: (operatorId: StringOrNumber, driverId: string) =>
    [...operatorsQueryKeys.drivers(operatorId), 'invitation', driverId] as const,
  driversFiles: (operatorId: StringOrNumber, driverId: string) =>
    [...operatorsQueryKeys.driver(operatorId, driverId), 'files'] as const,
  driversRidesCandidates: (operatorId: StringOrNumber, rideId: string) =>
    [...operatorsQueryKeys.ride(operatorId, rideId), 'driver-candidates'] as const,
  driverCandidates: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'driver-candidates'] as const,
  pricingRules: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'pricing-rules'] as const,
  vehicleSizes: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'vehicle-sizes'] as const,
  vehicleClasses: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'vehicle-classes'] as const,
  pricingSeasonalAdjustments: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'pricing-seasonal-adjustments'] as const,
  pricingSeasonalAdjustmentsWithParams: (
    operatorId: StringOrNumber,
    params: PaginationQueryParams
  ) =>
    [
      ...operatorsQueryKeys.pricingSeasonalAdjustments(operatorId),
      'pricing-seasonal-adjustments',
      params,
    ] as const,
  calculator: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'calculator'] as const,
  operatorsClients: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'clients'] as const,
  operatorsClientsWithParams: (operatorId: StringOrNumber, params?: PaginationQueryParams) =>
    [...operatorsQueryKeys.detail(operatorId), 'clients', params] as const,
  operatorsClient: (operatorId: StringOrNumber, clientId: string) =>
    [...operatorsQueryKeys.operatorsClients(operatorId), clientId] as const,
  operatorsClientsAll: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.operatorsClients(operatorId), 'all'] as const,
  operatorsRides: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'rides'] as const,
  operatorsRidesClients: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.operatorsRides(operatorId), 'clients'] as const,
  operatorsRidesClient: (operatorId: StringOrNumber, clientId: string) =>
    [...operatorsQueryKeys.operatorsRidesClients(operatorId), clientId] as const,
  operatorsRidesClientExportCSVWithParams: (
    operatorId: StringOrNumber,
    clientId: string,
    params: ExportCSVQueryParams
  ) =>
    [
      ...operatorsQueryKeys.operatorsRidesClient(operatorId, clientId),
      'export',
      'csv',
      params,
    ] as const,
  operatorsRidesLegs: (operatorId: StringOrNumber, rideId: string) =>
    [...operatorsQueryKeys.ride(operatorId, rideId), 'legs'] as const,
  vehiclesRidesCandidates: (operatorId: StringOrNumber, rideId: string) =>
    [...operatorsQueryKeys.ride(operatorId, rideId), 'vehicle-candidates'] as const,
  operatorsOrders: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'orders'] as const,
  operatorsOrder: (operatorId: StringOrNumber, identifier: StringOrNumber) =>
    [...operatorsQueryKeys.operatorsOrders(operatorId), identifier] as const,
  operatorsBankAccount: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'bankaccount'] as const,
  operatorsAlerts: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'alerts'] as const,
  operatorsAlertsCount: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.operatorsAlerts(operatorId), 'count'] as const,
  operatorsAlertsWithParams: (
    operatorId: StringOrNumber,
    params: OperatorsAlertsPaginationQueryParams
  ) => [...operatorsQueryKeys.operatorsAlerts(operatorId), params] as const,
  operatorsAlert: (operatorId: StringOrNumber, alertId: string) =>
    [...operatorsQueryKeys.operatorsAlerts(operatorId), alertId] as const,
  operatorsAlertStatus: (operatorId: StringOrNumber, alertId: string) =>
    [...operatorsQueryKeys.operatorsAlert(operatorId, alertId), 'status'] as const,
  operatorsInvitation: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'invitation'] as const,
  userMessaging: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'usermessaging'] as const,
  userMessagingMessages: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.userMessaging(operatorId), 'messages'] as const,
  userMessagingMessagesWithParams: (
    operatorId: StringOrNumber,
    params: OperatorsUserMessagingMessagesQueryParams
  ) => [...operatorsQueryKeys.userMessagingMessages(operatorId), params] as const,
  userMessagingUsers: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.userMessaging(operatorId), 'users'] as const,
  userMessagingUsersWithParams: (
    operatorId: StringOrNumber,
    params: OperatorsUserMessagingUsersQueryParams
  ) => [...operatorsQueryKeys.userMessagingUsers(operatorId), 'users', params] as const,
  userMessagingGroups: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.userMessaging(operatorId), 'groups'] as const,
  userMessagingGroup: (operatorId: StringOrNumber, groupId: string) =>
    [...operatorsQueryKeys.userMessagingGroups(operatorId), groupId] as const,
  userMessagingGroupMembers: (operatorId: StringOrNumber, groupId: string) =>
    [...operatorsQueryKeys.userMessagingGroup(operatorId, groupId), 'members'] as const,
  userMessagingGroupArchive: (operatorId: StringOrNumber, groupId: string) =>
    [...operatorsQueryKeys.userMessagingGroup(operatorId, groupId), 'archive'] as const,
  operatorsOffers: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'offers'] as const,
  operatorsOffersPrice: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.operatorsOffers(operatorId), 'price'] as const,
  operatorsOffersEmail: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.operatorsOffers(operatorId), 'email'] as const,
  fileDownload: () => [...operatorsQueryKeys.all, 'file-download'] as const,
  operatorsCorporateCharters: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'corporate-charters'] as const,
  operatorsOperatorToken: (accessToken: string) =>
    [...operatorsQueryKeys.all, 'operator-token', accessToken] as const,
  operatorsActivePlans: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'plans', 'active'] as const,
  operatorsMileageRevenueSummary: (operatorId: StringOrNumber) =>
    [...operatorsQueryKeys.detail(operatorId), 'mileage-revenue-summary'] as const,
};

// We could use it to fetch a different operator but we only access the current operator in this application
export function useOperatorsQuery() {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.detail(operatorId),
    queryFn: () => fetchOperatorsById(String(operatorId)),
    enabled: operatorId != null,
    select: ({ data }) => data,
  });
}

export function usePutOperatorsMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<OperatorDtoSchemaType, AxiosError, OperatorDtoSchemaType>({
    mutationKey: operatorsQueryKeys.detail(operatorId),
    mutationFn: (data) => putOperatorsById(String(operatorId), data).then((d) => d.data),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.detail(operatorId) });
    },
  });
}

export function useOperatorsUsersQuery(params: OperatorsListPaginationQueryParams) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.usersWithParams(operatorId, params),
    queryFn: () => fetchOperatorsUsers(String(operatorId), params),
    placeholderData: keepPreviousData,
    select: ({ data }) => data,
  });
}

type OperatorsUsersRolesMutationProps = {
  id?: string;
  role?: UserRole;
};

export function useOperatorsUsersRolesMutation(id?: OperatorsUsersRolesMutationProps['id']) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<number, AxiosError, OperatorsUsersRolesMutationProps>({
    mutationKey: operatorsQueryKeys.userRoles(operatorId, id!),
    mutationFn: (v) =>
      postOperatorsUsersRoles(String(operatorId), v.id!, v.role!).then((d) => d.status),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.users(operatorId) });
    },
  });
}

export function useDeleteOperatorsUsersMutation(id?: OperatorsUsersRolesMutationProps['id']) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<number, AxiosError, { id?: OperatorsUsersRolesMutationProps['id'] }>({
    mutationKey: operatorsQueryKeys.user(operatorId, id!),
    mutationFn: (data) => deleteOperatorsById(String(operatorId), data.id!).then((d) => d.status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.users(operatorId) });
    },
  });
}

export function useOperatorsScheduleVehiclesQuery(params?: ScheduleQueryParams) {
  const operatorId = useOperatorId();
  return useQuery({
    queryKey: operatorsQueryKeys.scheduleVehiclesWithParams(operatorId, params!),
    queryFn: () => fetchOperatorsScheduleVehicles(String(operatorId), params!),
    select: ({ data }) => data,
  });
}

export function useOperatorsScheduleVehiclesRepairLogQuery(
  vehicleId: string,
  params: PaginationQueryParams
) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.scheduleVehiclesRepairLog(operatorId, vehicleId),
    queryFn: () => fetchOperatorsScheduleVehiclesRepairLog(String(operatorId), vehicleId, params!),
    enabled: vehicleId != null,
    select: ({ data }) => data,
  });
}

export function useOperatorsScheduleDriversVacationLogQuery(
  driverId: string,
  params: PaginationQueryParams
) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.scheduleDriversVacationLog(operatorId, driverId, params),
    queryFn: () => fetchOperatorsScheduleDriversVacationLog(String(operatorId), driverId, params!),
    enabled: driverId != null,
    select: ({ data }) => data,
  });
}

export function useOperatorsScheduleDriversQuery(params?: ScheduleQueryParams) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.scheduleDrivers(operatorId, params!),
    queryFn: () => fetchOperatorsScheduleDrivers(String(operatorId), params!),
    enabled: params != null,
    select: ({ data }) => data,
  });
}

export function useOperatorsScheduleDriversWorkQuery(params?: ScheduleQueryParams) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.scheduleDriversWork(operatorId, params!),
    queryFn: () => fetchOperatorsScheduleDriversWork(String(operatorId), params!),
    enabled: params != null,
    select: ({ data }) => data,
  });
}

export function useOperatorOffersQuery(params: PaginationQueryParams) {
  const operatorId = useOperatorId();
  return useQuery({
    queryKey: operatorsQueryKeys.offers(operatorId, params),
    queryFn: () => fetchOperatorOffersByOperatorId(String(operatorId), params),
    enabled: params != null,
    select: ({ data }) => data,
  });
}

// identifier can be orderId or bookingNumber
export function useOperatorsOrdersQuery(identifier?: StringOrNumber) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.order(operatorId!, identifier!),
    queryFn: () => fetchOperatorsOrdersById(String(operatorId), String(identifier)),
    enabled: identifier != null,
    select: ({ data }) => data,
  });
}

export function usePatchOperatorsOrdersDetailsMutation(identifier: StringOrNumber) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<OrderDtoSchemaType, AxiosError, OrderDetailsSchemaPayloadType>({
    mutationKey: operatorsQueryKeys.order(operatorId, identifier),
    mutationFn: (data) =>
      patchOperatorsOrdersDetails(String(operatorId), identifier, data).then((d) => d.data),
    onSuccess: () => {
      // For simplicity sake just invalidate the order
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.order(operatorId, identifier) });
    },
  });
}

export function usePostOperatorsScheduleMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<ScheduleItem, AxiosError, ScheduleItemSchemaType>({
    mutationKey: operatorsQueryKeys.schedule(operatorId),
    mutationFn: (data) => postOperatorsSchedule(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // For simplicity sake just invalidate the schedule which in turn will re-query the schedule with current params
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.schedule(operatorId!) });
    },
  });
}

interface ScheduleItemSchemaWithResourceId extends ScheduleItemSchemaType {
  resourceId?: number;
}

export function usePutOperatorsScheduleMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<ScheduleItem, AxiosError, ScheduleItemSchemaWithResourceId>({
    mutationKey: operatorsQueryKeys.schedule(operatorId),
    mutationFn: (data) =>
      putOperatorsSchedule(String(operatorId), String(data.resourceId), data).then((d) => d.data),
    onSuccess: () => {
      // For simplicity sake just invalidate the schedule which in turn will re-query the schedule with current params
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.schedule(operatorId!) });
    },
  });
}

export function useDeleteOperatorsScheduleMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<number, AxiosError, { id?: string }>({
    mutationKey: operatorsQueryKeys.schedule(operatorId),
    mutationFn: (data) => deleteScheduleById(String(operatorId), data.id!).then((d) => d.status),
    onSuccess: () => {
      // For simplicity sake just invalidate the schedule which in turn will re-query the schedule with current params
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.schedule(operatorId!) });
    },
  });
}

export function useOperatorsInvoicesQuery(params: OperatorsInvoicesPaginationQueryParams) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.invoices(operatorId, params),
    queryFn: () => fetchOperatorsInvoices(String(operatorId), params),
    placeholderData: keepPreviousData,
    select: ({ data }) => data,
  });
}

export function useOperatorsOrdersPricingDetailsQuery(bookingNumber: string) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.pricingDetails(operatorId, bookingNumber),
    queryFn: () => fetchOperatorsOrdersPricingDetails(String(operatorId), bookingNumber),
    enabled: bookingNumber != null,
    placeholderData: keepPreviousData,
    select: ({ data }) => data,
  });
}

export function useOperatorsRidesPricingDetailsQuery(rideId: number) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.pricingDetails(operatorId, rideId),
    queryFn: () => fetchOperatorsRidesPricingDetails(String(operatorId), rideId),
    enabled: rideId != null,
    placeholderData: keepPreviousData,
    select: ({ data }) => data,
  });
}

export function useOperatorsRidesActiveQuery(params: PaginationQueryParams) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.ridesActive(operatorId, params),
    queryFn: () => fetchOperatorsRidesActive(String(operatorId), params),
    placeholderData: keepPreviousData,
    select: ({ data }) => data,
  });
}

export function useOperatorsRidesFilterQuery(
  params: PaginationQueryParams & {
    from?: string;
    onlyUpcoming?: boolean;
    onlyCompleted?: boolean;
    onlyActive?: boolean;
  }
) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.ridesFilter(operatorId, params),
    queryFn: () => fetchOperatorsRidesFilter(String(operatorId), params),
    placeholderData: keepPreviousData,
    select: ({ data }) => data,
  });
}

export function useOperatorsVehiclesFilterQuery(params: OperatorsVehiclesPaginationQueryParams) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.vehiclesFilter(operatorId, params),
    queryFn: () => fetchOperatorsVehicles(String(operatorId), params),
    placeholderData: keepPreviousData,
    select: ({ data }) => data,
  });
}

export function useOperatorsDriversFilterQuery(params: OperatorsDriversPaginationQueryParams) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.driversFilter(operatorId, params),
    queryFn: () => fetchOperatorsDrivers(String(operatorId), params),
    placeholderData: keepPreviousData,
    select: ({ data }) => data,
  });
}

export function useOperatorsDriversQuery(driverId?: string) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.driver(operatorId, driverId!),
    queryFn: () => fetchOperatorsDriversById(String(operatorId), driverId!),
    enabled: driverId != null,
    staleTime: STALE_FIVE_MINUTES,
    select: ({ data }) => data,
  });
}

export function useOperatorsDriversFilesQuery(driverId: string) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.driversFiles(operatorId, driverId),
    queryFn: () => fetchOperatorsDriversFiles(String(operatorId), driverId),
    staleTime: STALE_ONE_HOUR,
    select: ({ data }) => data,
  });
}

export function useOperatorsRidesFinishMutation(rideId: string) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<RideDtoSchemaType, AxiosError, ChangeRideEndSchemaType>({
    mutationKey: operatorsQueryKeys.ridesFinish(operatorId, rideId),
    mutationFn: (data) => postOperatorsRidesFinish(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate all rides
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.rides(operatorId) });
    },
  });
}

export function useOperatorsPricingRulesQuery() {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.pricingRules(operatorId),
    queryFn: () => fetchOperatorsPricingRules(String(operatorId)),
    select: ({ data }) => data,
    staleTime: STALE_ONE_HOUR,
  });
}

export function useOperatorsPricingRulesMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<PricingRulesDtoSchemaType, AxiosError, CarrierPricingCostsSchemaType>({
    mutationKey: operatorsQueryKeys.pricingRules(operatorId),
    mutationFn: (data) =>
      putOperatorsPricingRules(String(operatorId), { ...data, operatorId }).then((d) => d.data),
    onSuccess: () => {
      // Invalidate pricing rules
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.pricingRules(operatorId) });
    },
  });
}

export function useOperatorsVehicleSizesQuery() {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.vehicleSizes(operatorId),
    queryFn: () => fetchOperatorsVehicleSizes(String(operatorId)),
    select: ({ data }) => data,
    staleTime: STALE_ONE_HOUR,
  });
}

export function useOperatorsVehicleClassesQuery() {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.vehicleClasses(operatorId),
    queryFn: () => fetchOperatorsVehicleClasses(String(operatorId)),
    select: ({ data }) => data,
    staleTime: STALE_ONE_HOUR,
  });
}

export function useOperatorsPricingSeasonalAdjustmentsQuery(params: PaginationQueryParams) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.pricingSeasonalAdjustmentsWithParams(operatorId, params),
    queryFn: () => fetchOperatorsPricingSeasonalAdjustments(String(operatorId), params),
    placeholderData: keepPreviousData,
    select: ({ data }) => data,
  });
}

export function useOperatorsPricingSeasonalAdjustmentsPostMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<PricingPeriodSchemaType, AxiosError, PricingPeriodSchemaType>({
    mutationKey: operatorsQueryKeys.pricingSeasonalAdjustments(operatorId),
    mutationFn: (data) =>
      postOperatorsPricingSeasonalAdjustments(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate pricing seasonal adjustments
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.pricingSeasonalAdjustments(operatorId),
      });
    },
  });
}

export function useOperatorsPricingSeasonalAdjustmentsPutMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<PricingPeriodSchemaType, AxiosError, PricingPeriodSchemaType>({
    mutationKey: operatorsQueryKeys.pricingSeasonalAdjustments(operatorId),
    mutationFn: (data) =>
      putOperatorsPricingSeasonalAdjustments(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate pricing seasonal adjustments
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.pricingSeasonalAdjustments(operatorId),
      });
    },
  });
}

export function useDeleteOperatorsPricingSeasonalAdjustmentsMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<number, AxiosError, { id: string }>({
    mutationKey: operatorsQueryKeys.pricingSeasonalAdjustments(operatorId),
    mutationFn: (data) =>
      deleteOperatorsPricingSeasonalAdjustments(String(operatorId), data.id).then((d) => d.status),
    onSuccess: () => {
      // Invalidate pricing seasonal adjustments
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.pricingSeasonalAdjustments(operatorId),
      });
    },
  });
}

export function useOperatorsCalculatorMutation() {
  const operatorId = useOperatorId();

  return useMutation<CalculatorResponseDtoSchemaType[], AxiosError, PricingCalculatorSchemaType>({
    mutationKey: operatorsQueryKeys.calculator(operatorId),
    mutationFn: (data) => postOperatorsCalculator(String(operatorId), data).then((d) => d.data),
  });
}

function handleTriggerDownloadWithBlob(response: AxiosResponse<string>) {
  let fileName = '';
  if (response.headers != null) {
    const name = response.headers?.['content-disposition']
      ?.split('filename=')?.[1] // gets the filename key
      ?.split(';')?.[0] // gets the value of the key
      ?.replace(/^"(.+)"$/, '$1'); // replaces quotes from start and end
    if (name != null) fileName = name;
  }

  triggerDownloadLink(new Blob([response.data]), fileName);
}

export function useOperatorsDownloadFileByUrlQuery() {
  return useMutation<AxiosResponse<string>, AxiosError, string>({
    mutationKey: operatorsQueryKeys.fileDownload(),
    mutationFn: (url) => fetchOperatorsFileDownloadUrl(url).then((d) => d),
    onSuccess: (response) => {
      handleTriggerDownloadWithBlob(response);
    },
  });
}

export function useOperatorsRidesClientsExportCsvQuery(
  clientId: string,
  params?: ExportCSVQueryParams
) {
  const operatorId = useOperatorId();

  const isEnabled = params != null;
  return useQuery({
    queryKey: operatorsQueryKeys.operatorsRidesClientExportCSVWithParams(
      operatorId,
      clientId!,
      params!
    ),
    queryFn: async () => {
      const response = await fetchOperatorsRidesClientsExportCSV(
        String(operatorId),
        clientId!,
        params!
      );
      if (response.status === 200) handleTriggerDownloadWithBlob(response);
      return response;
    },
    enabled: isEnabled,
    select: ({ data }) => data,
  });
}

export function useDeleteOperatorsRidesLegsMutation(rideId: string) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<RideWithScheduleItem, AxiosError, number>({
    mutationKey: operatorsQueryKeys.operatorsRidesLegs(operatorId, rideId),
    mutationFn: (legIndex) =>
      deleteOperatorsRidesLegsByIndex(String(operatorId), rideId, legIndex).then((d) => d.data),
    onSuccess: () => {
      // Invalidate ride
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.ride(operatorId, rideId) });
      // Invalidate schedule
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.schedule(operatorId) });
    },
  });
}

export function usePostOperatorsRidesLegsMutation(rideId: string) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<RideWithScheduleItem, AxiosError, LegSchemaType & { legIndex: number }>({
    mutationKey: operatorsQueryKeys.operatorsRidesLegs(operatorId, rideId),
    mutationFn: (data) =>
      postOperatorsRidesLegsByIndex(String(operatorId), rideId, data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate ride
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.ride(operatorId, rideId) });
      // Invalidate schedule
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.schedule(operatorId) });
    },
  });
}

export function useDeleteOperatorsOrdersMutation(identifier: StringOrNumber) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<number, AxiosError>({
    mutationKey: operatorsQueryKeys.operatorsOrder(operatorId, identifier),
    mutationFn: () =>
      deleteOperatorsOrdersByIdentifier(String(operatorId), identifier).then((d) => d.status),
    onSuccess: () => {
      // Invalidate order
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.order(operatorId, identifier) });
      // Invalidate schedule
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.schedule(operatorId) });
    },
  });
}

export function useOperatorsFilesQuery() {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.operatorsFiles(operatorId),
    queryFn: () => fetchOperatorsFiles(String(operatorId)),
    staleTime: STALE_ONE_HOUR,
    select: ({ data }) => data,
  });
}

export function useDeleteOperatorsFilesMutation() {
  const operatorId = useOperatorId();
  const queryClient = useQueryClient();

  return useMutation<number, AxiosError, { fileName: string }>({
    mutationKey: operatorsQueryKeys.operatorsFiles(operatorId),
    mutationFn: (data) =>
      deleteOperatorsFilesByFileName(String(operatorId), data.fileName).then((d) => d.status),
    onSuccess: () => {
      // Invalidate operators files
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.operatorsFiles(operatorId) });
    },
  });
}

export function usePostOperatorsFilesMutation() {
  const operatorId = useOperatorId();
  const queryClient = useQueryClient();

  return useMutation<FileType[], AxiosError, FormData>({
    mutationKey: operatorsQueryKeys.operatorsFiles(operatorId),
    mutationFn: (data) => postOperatorsFiles(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate operators files
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.operatorsFiles(operatorId) });
    },
  });
}

export function useOperatorsBankAccountQuery() {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.operatorsBankAccount(operatorId),
    queryFn: () => fetchOperatorsBankAccount(String(operatorId)),
    staleTime: STALE_ONE_HOUR,
    select: ({ data }) => data,
  });
}

export function usePostOperatorsBankAccountMutation() {
  const operatorId = useOperatorId();
  const queryClient = useQueryClient();

  return useMutation<
    OperatorBankAccountDtoSchemaType,
    AxiosError,
    OperatorBankAccountDtoSchemaType
  >({
    mutationKey: operatorsQueryKeys.operatorsBankAccount(operatorId),
    mutationFn: (data) => putOperatorsBankAccount(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate operators bank account
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.operatorsBankAccount(operatorId),
      });
    },
  });
}

export function useDeleteOperatorsBankAccountMutation() {
  const operatorId = useOperatorId();
  const queryClient = useQueryClient();

  return useMutation<number, AxiosError>({
    mutationKey: operatorsQueryKeys.operatorsBankAccount(operatorId),
    mutationFn: () => deleteOperatorsBankAccount(String(operatorId)).then((d) => d.status),
    onSuccess: () => {
      // Invalidate operators bank account
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.operatorsBankAccount(operatorId),
      });
    },
  });
}

export function useOperatorsVehiclesQuery(vehicleId: string) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.vehicle(operatorId, vehicleId),
    queryFn: () => fetchOperatorsVehiclesById(String(operatorId), vehicleId),
    enabled: vehicleId != null,
    staleTime: STALE_FIVE_MINUTES,
    select: ({ data }) => data,
  });
}

export function useOperatorsVehiclesFilesQuery(vehicleId: string) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.vehiclesFiles(operatorId, vehicleId),
    queryFn: () => fetchOperatorsVehiclesFiles(String(operatorId), vehicleId),
    staleTime: STALE_ONE_HOUR,
    select: ({ data }) => data,
  });
}

export function usePostOperatorsVehiclesFilesPhotosRankMutation(vehicleId: string) {
  const operatorId = useOperatorId();
  const queryClient = useQueryClient();

  return useMutation<number, AxiosError, FileResponse[]>({
    mutationKey: operatorsQueryKeys.vehiclesFilesPhotosRank(operatorId, vehicleId),
    mutationFn: (data) =>
      postOperatorsVehiclesFilesPhotosRank(String(operatorId), vehicleId, data).then(
        (d) => d.status
      ),
    onSuccess: () => {
      // Invalidate vehicle files
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.vehiclesFiles(operatorId, vehicleId),
      });
    },
  });
}

export function useOperatorsVehiclesTemporaryBaseQuery(
  vehicleId: string,
  params: PaginationQueryParams
) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.vehiclesTemporaryBaseWithParams(operatorId, vehicleId, params),
    queryFn: () => fetchOperatorsVehiclesTemporaryBase(String(operatorId), vehicleId, params),
    enabled: vehicleId != null,
    staleTime: STALE_ONE_HOUR,
    select: ({ data }) => data,
  });
}

export function useOperatorsRidesVehicleCandidatesQuery(rideId: string, enabled = true) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.vehiclesRidesCandidates(operatorId, rideId),
    queryFn: () => fetchOperatorsRidesVehicleCandidates(String(operatorId), rideId),
    staleTime: STALE_FIVE_MINUTES,
    enabled,
    select: ({ data }) => data,
  });
}

export function useOperatorsRidesDriverCandidatesQuery(rideId: string, enabled = true) {
  const operatorId = useOperatorId();

  const isEnabled = rideId != null && enabled;
  return useQuery({
    queryKey: operatorsQueryKeys.driversRidesCandidates(operatorId, rideId),
    queryFn: () => fetchOperatorsRidesDriverCandidates(String(operatorId), rideId),
    staleTime: STALE_FIVE_MINUTES,
    enabled: isEnabled,
    select: ({ data }) => data,
  });
}

// TODO: REFETCH THIS WHEN DRIVERS IS UPDATED
export function useOperatorsDriverCandidatesQuery(enabled = true) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.driverCandidates(operatorId),
    queryFn: () => fetchOperatorsDriversCandidates(String(operatorId)),
    staleTime: STALE_FIVE_MINUTES,
    enabled,
    select: ({ data }) => data,
  });
}

export function usePostOperatorsVehiclesDriversMutation(vehicleId: string) {
  const operatorId = useOperatorId();
  const queryClient = useQueryClient();

  return useMutation<VehicleDtoSchema, AxiosError, { driverId: string }>({
    mutationKey: operatorsQueryKeys.vehiclesFilesPhotosRank(operatorId, vehicleId),
    mutationFn: (data) =>
      postOperatorsVehiclesDriversById(String(operatorId), vehicleId, data.driverId).then(
        (d) => d.data
      ),
    onSuccess: () => {
      // Invalidate vehicle
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.vehicle(operatorId, vehicleId),
      });
    },
  });
}

export function useOperatorsVehiclesAllQuery(enabled = true) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.vehiclesAll(operatorId),
    queryFn: () => fetchOperatorsVehiclesAll(String(operatorId)),
    staleTime: STALE_FIVE_MINUTES,
    enabled,
    select: ({ data }) => data,
  });
}

export function useOperatorsAlertsQuery(params: OperatorsAlertsPaginationQueryParams) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.operatorsAlertsWithParams(operatorId, params),
    queryFn: () => fetchOperatorsAlerts(String(operatorId), params),
    staleTime: STALE_FIVE_MINUTES,
    select: ({ data }) => data,
  });
}

export function usePostOperatorsAlertsStatusMutation(alertId: string) {
  const operatorId = useOperatorId();
  const queryClient = useQueryClient();

  return useMutation<number, AxiosError, SeenStatus>({
    mutationKey: operatorsQueryKeys.operatorsAlertStatus(operatorId, alertId),
    mutationFn: (data) =>
      postOperatorsAlertsStatus(String(operatorId), alertId, data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate alerts
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.operatorsAlerts(operatorId) });
    },
  });
}

export function useOperatorsUserMessagingMessagesQuery(
  params: OperatorsUserMessagingMessagesQueryParams
) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.userMessagingMessagesWithParams(operatorId, params),
    queryFn: () => fetchOperatorsUserMessagingMessages(String(operatorId), params),
    staleTime: STALE_FIVE_MINUTES,
    select: ({ data }) => data,
  });
}

export function useOperatorsUserMessagingUsersQuery(
  params: OperatorsUserMessagingUsersQueryParams
) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.userMessagingUsersWithParams(operatorId, params),
    queryFn: () => fetchOperatorsUserMessagingUsers(String(operatorId), params),
    staleTime: STALE_FIVE_MINUTES,
    select: ({ data }) => data,
  });
}

export function useOperatorsUserMessagingGroupsQuery() {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.userMessagingGroups(operatorId),
    queryFn: () => fetchOperatorsUserMessagingGroups(String(operatorId)),
    staleTime: STALE_FIVE_MINUTES,
    select: ({ data }) => data,
  });
}

export function useOperatorsUserMessagingGroupsMembersQuery(groupId: string) {
  const operatorId = useOperatorId();

  return useQuery({
    queryKey: operatorsQueryKeys.userMessagingGroupMembers(operatorId, groupId),
    queryFn: () => fetchOperatorsUserMessagingGroupsMembers(String(operatorId), groupId),
    staleTime: STALE_FIVE_MINUTES,
    select: ({ data }) => data,
  });
}

export function usePostOperatorsUserMessagingGroupsMembersMutation(groupId: string) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<MessageGroupSchemaType[], AxiosError, number[]>({
    mutationKey: operatorsQueryKeys.userMessagingGroupMembers(operatorId, groupId),
    mutationFn: (data) =>
      postOperatorsUserMessagingGroupsMembers(String(operatorId), groupId, data).then(
        (d) => d.data
      ),
    onSuccess: () => {
      // Invalidate groups
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.userMessagingGroups(operatorId),
      });
    },
  });
}

export function usePostOperatorsOffersMutation() {
  const operatorId = useOperatorId();
  const queryClient = useQueryClient();

  return useMutation<OfferSchemaType, AxiosError, RideOffersSchemaType>({
    mutationKey: operatorsQueryKeys.operatorsOffers(operatorId),
    mutationFn: (data) => postOperatorsOffers(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate offers
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.operatorsOffers(operatorId) });
    },
  });
}

export function usePostOperatorsOffersPriceMutation() {
  const operatorId = useOperatorId();

  return useMutation<OfferSchemaType, AxiosError, OfferPriceRequest>({
    mutationKey: operatorsQueryKeys.operatorsOffersPrice(operatorId),
    mutationFn: (data) => postOperatorsOffersPrice(String(operatorId), data).then((d) => d.data),
  });
}

export function usePostOperatorsOffersEmailMutation() {
  const operatorId = useOperatorId();

  return useMutation<OfferSchemaType, AxiosError, SendOfferToClientRequestSchemaType>({
    mutationKey: operatorsQueryKeys.operatorsOffersEmail(operatorId),
    mutationFn: (data) => postOperatorsOffersEmail(String(operatorId), data).then((d) => d.data),
  });
}

export function usePostOperatorsVehiclesActivationMutation() {
  const operatorId = useOperatorId();
  const queryClient = useQueryClient();

  return useMutation<BusStatus, AxiosError, VehicleActivationDto>({
    mutationKey: operatorsQueryKeys.vehiclesActivation(operatorId),
    mutationFn: (data) =>
      postOperatorsVehiclesActivation(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate vehicles
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.vehicles(operatorId) });
    },
  });
}

export function usePostOperatorsInvitationMutation() {
  const operatorId = useOperatorId();

  return useMutation<number, AxiosError, AccountUserSchemaType>({
    mutationKey: operatorsQueryKeys.operatorsInvitation(operatorId),
    mutationFn: (data) => postOperatorsInvitation(String(operatorId), data).then((d) => d.status),
  });
}

export function useDeleteOperatorsUserMessagingGroupsMembersMutation(groupId: string) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<number, AxiosError, string>({
    mutationKey: operatorsQueryKeys.userMessagingGroupMembers(operatorId, groupId),
    mutationFn: (data) =>
      deleteOperatorsUserMessagingGroupsMembersById(String(operatorId), groupId, data).then(
        (d) => d.status
      ),
    onSuccess: () => {
      // Invalidate group members
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.userMessagingGroupMembers(operatorId, groupId),
      });
    },
  });
}

export function usePutOperatorsUserMessagingGroupsMutation(groupId: string) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<MessageGroupSchemaType, AxiosError, MessageGroupSchemaType>({
    mutationKey: operatorsQueryKeys.userMessagingGroup(operatorId, groupId),
    mutationFn: (data) =>
      putOperatorsUserMessagingGroupsById(String(operatorId), groupId, data).then((d) => d.data),
    onSuccess: async () => {
      // Invalidate groups
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.userMessagingGroups(operatorId),
      });
    },
  });
}

export function usePostOperatorsUserMessagingGroupsArchiveMutation(groupId: string) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<number, AxiosError>({
    mutationKey: operatorsQueryKeys.userMessagingGroupArchive(operatorId, groupId),
    mutationFn: () =>
      postOperatorsUserMessagingGroupsArchive(String(operatorId), groupId).then((d) => d.status),
    onSuccess: () => {
      // Invalidate groups
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.userMessagingGroups(operatorId),
      });
    },
  });
}

export function usePostOperatorsRidesMoveMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<RideWithScheduleItem, AxiosError, RideMoveRequest>({
    mutationKey: operatorsQueryKeys.rides(operatorId),
    mutationFn: (data) =>
      postOperatorsRidesMove(String(operatorId), data.rideId, data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate all rides
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.rides(operatorId) });
      // Invalidate schedule
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.schedule(operatorId) });
    },
  });
}

export function usePostOperatorsRidesDriversMutation(rideId: string) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<RideWithScheduleItem, AxiosError, string>({
    mutationKey: operatorsQueryKeys.rideDrivers(operatorId, rideId),
    mutationFn: (data) =>
      postOperatorsRidesDriversMove(String(operatorId), rideId, data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate all rides
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.rides(operatorId) });
      // Invalidate schedule
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.schedule(operatorId) });
    },
  });
}

export function usePostOperatorsUserMessagingGroupsMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<MessageGroupSchemaType[], AxiosError, NewMessageGroupSchemaType>({
    mutationKey: operatorsQueryKeys.userMessagingGroups(operatorId),
    mutationFn: (data) =>
      postOperatorsUserMessagingGroups(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate groups
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.userMessagingGroups(operatorId),
      });
    },
  });
}

export function usePostOperatorsUserMessagingMessagesMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<UserMessageDtoSchemaType, AxiosError, UserMessageDtoSchemaType>({
    mutationKey: operatorsQueryKeys.userMessagingGroups(operatorId),
    mutationFn: (data) =>
      postOperatorsUserMessagingMessages(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate messages
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.userMessagingMessages(operatorId),
      });
    },
  });
}

export function usePostOperatorsDriversMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<DriverWithStatusDtoSchemaType, AxiosError, DriverDtoSchema>({
    mutationKey: operatorsQueryKeys.drivers(operatorId),
    mutationFn: (data) => postOperatorsDrivers(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate drivers
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.drivers(operatorId) });
      // Invalidate driver candidates
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.driverCandidates(operatorId) });
    },
  });
}

export function usePutOperatorsDriversMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<DriverDtoSchema, AxiosError, DriverDtoSchema>({
    mutationKey: operatorsQueryKeys.drivers(operatorId),
    mutationFn: (data) => putOperatorsDrivers(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate drivers
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.drivers(operatorId) });
    },
  });
}

export function usePostOperatorsVehiclesMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<VehicleWithStatusDtoSchema, AxiosError, VehicleDtoSchema>({
    mutationKey: operatorsQueryKeys.vehicles(operatorId),
    mutationFn: (data) => postOperatorsVehicles(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate vehicles
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.vehicles(operatorId) });
      // Invalidate vehicle classes
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.vehicleClasses(operatorId) });
      // Invalidate vehicle sizes
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.vehicleSizes(operatorId) });
    },
  });
}

export function usePutOperatorsVehiclesMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<VehicleDtoSchema, AxiosError, VehicleDtoSchema>({
    mutationKey: operatorsQueryKeys.vehicles(operatorId),
    mutationFn: (data) => putOperatorsVehicles(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate vehicles
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.vehicles(operatorId) });
      // Invalidate vehicle classes
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.vehicleClasses(operatorId) });
      // Invalidate vehicle sizes
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.vehicleSizes(operatorId) });
    },
  });
}

export function usePostOperatorsVehiclesTemporaryBaseMutation(vehicleId: string) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<
    VehicleTemporaryBaseDtoSchemaType,
    AxiosError,
    VehicleTemporaryBaseDtoSchemaType
  >({
    mutationKey: operatorsQueryKeys.vehiclesTemporaryBase(operatorId, vehicleId),
    mutationFn: (data) =>
      postOperatorsVehiclesTemporaryBase(String(operatorId), vehicleId, data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate vehicle temporary base
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.vehiclesTemporaryBase(operatorId, vehicleId),
      });
    },
  });
}

export function usePutOperatorsVehiclesTemporaryBaseMutation(vehicleId: string) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<
    VehicleTemporaryBaseDtoSchemaType,
    AxiosError,
    VehicleTemporaryBaseDtoSchemaType
  >({
    mutationKey: operatorsQueryKeys.vehiclesTemporaryBase(operatorId, vehicleId),
    mutationFn: (data) =>
      putOperatorsVehiclesTemporaryBase(String(operatorId), vehicleId, data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate vehicle temporary base
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.vehiclesTemporaryBase(operatorId, vehicleId),
      });
    },
  });
}

export function useDeleteOperatorsVehiclesTemporaryBaseMutation(vehicleId: string) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<number, AxiosError, string>({
    mutationKey: operatorsQueryKeys.vehiclesTemporaryBase(operatorId, vehicleId),
    mutationFn: (data) =>
      deleteOperatorsVehiclesTemporaryBase(String(operatorId), vehicleId, data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate vehicle temporary base
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.vehiclesTemporaryBase(operatorId, vehicleId),
      });
    },
  });
}

export function useDeleteOperatorsVehiclesMutation(vehicleId: string) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<number, AxiosError, string>({
    mutationKey: operatorsQueryKeys.vehicle(operatorId, vehicleId),
    mutationFn: () =>
      deleteOperatorsVehiclesById(String(operatorId), vehicleId).then((d) => d.status),
    onSuccess: () => {
      // Invalidate vehicles
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.vehicles(operatorId) });
    },
  });
}

export function useOperatorsAlertsCountNewQuery() {
  const operatorId = useOperatorId();
  const { data: userInfo } = useUsersCurrentAccessesQuery();

  const isRequestDisabledByPermissions = [
    UserPermission.NO_ACCESS,
    UserPermission.RESTRICTED,
  ].includes(userInfo?.permissions?.ALERT as UserPermission);

  const enabled = operatorId != null && !isRequestDisabledByPermissions;
  return useQuery({
    queryKey: operatorsQueryKeys.operatorsAlertsCount(operatorId),
    queryFn: () => fetchOperatorsAlertsCountNew(String(operatorId)),
    staleTime: STALE_ONE_HOUR,
    enabled, // Gets triggered on logout - this prevents it
    select: ({ data }) => data,
  });
}

export function usePostOperatorsOrdersMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<OrderDtoSchemaType, AxiosError, OrderRequestPayload>({
    mutationKey: operatorsQueryKeys.orders(operatorId),
    mutationFn: (data) => postOperatorOrders(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate orders
      queryClient.invalidateQueries({
        queryKey: operatorsQueryKeys.orders(operatorId),
      });
      // Invalidate schedule
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.schedule(operatorId) });
    },
  });
}

export function useOperatorsVehiclesLocationsQuery(params?: OperatorsVehiclesLocationsQueryParams) {
  const operatorId = useOperatorId();

  const isEnabled = params != null;
  return useQuery({
    queryKey: operatorsQueryKeys.vehiclesLocationsFilter(operatorId, params!),
    queryFn: () => fetchOperatorsVehiclesLocations(String(operatorId), params!),
    staleTime: STALE_ONE_HOUR,
    enabled: isEnabled,
    select: ({ data }) => data,
  });
}

export function usePostOperatorsDriversInvitationMutation(driverId: string) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<number, AxiosError>({
    mutationKey: operatorsQueryKeys.driversInvitation(operatorId, driverId),
    mutationFn: () =>
      postOperatorsDriversInvitation(String(operatorId), driverId).then((d) => d.data),
    onSuccess: () => {
      // Invalidate drivers
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.drivers(operatorId) });
    },
  });
}

export function useDeleteOperatorsDriversMutation(driverId: string) {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<number, AxiosError>({
    mutationKey: operatorsQueryKeys.driver(operatorId, driverId),
    mutationFn: () =>
      deleteOperatorsDriversById(String(operatorId), driverId).then((d) => d.status),
    onSuccess: () => {
      // Invalidate drivers
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.drivers(operatorId) });
    },
  });
}

export function usePostOperatorsCorporateChartersMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<CorporateCharter, AxiosError, CorporateCharterRequest>({
    mutationKey: operatorsQueryKeys.operatorsCorporateCharters(operatorId),
    mutationFn: (data) =>
      postOperatorsCorporateCharters(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate schedule
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.schedule(operatorId) });
    },
  });
}

export function useOperatorsOperatorToken(accessToken?: string) {
  return useQuery({
    queryKey: operatorsQueryKeys.operatorsOperatorToken(accessToken!),
    queryFn: () => fetchOperatorsOperatorTokenByAccessToken(accessToken!),
    enabled: accessToken != null,
    staleTime: STALE_FIVE_MINUTES,
    select: ({ data }) => data,
  });
}

export function useOperatorsActivePlansQuery() {
  const operatorId = useOperatorId();
  return useQuery({
    queryKey: operatorsQueryKeys.operatorsActivePlans(operatorId),
    queryFn: () => fetchOperatorsPlansActive(String(operatorId)),
    enabled: operatorId != null,
    staleTime: STALE_FIVE_MINUTES,
    select: ({ data }) => data,
  });
}

export function useOperatorsMileageRevenueSummaryQuery() {
  const operatorId = useOperatorId();
  return useQuery({
    queryKey: operatorsQueryKeys.operatorsMileageRevenueSummary(operatorId),
    queryFn: () => fetchOperatorsMileageRevenueSummary(String(operatorId)),
    enabled: operatorId != null,
    staleTime: STALE_FIVE_MINUTES,
    select: ({ data }) => data,
  });
}

export function useOperatorsClientsQuery(params: PaginationQueryParams) {
  const operatorId = useOperatorId();
  return useQuery({
    queryKey: operatorsQueryKeys.operatorsClientsWithParams(operatorId, params),
    queryFn: () => fetchOperatorsClients(String(operatorId), params),
    enabled: operatorId != null,
    staleTime: STALE_FIVE_MINUTES,
    select: ({ data }) => data,
  });
}

export function useOperatorsClientsAllQuery(enabled = true) {
  const operatorId = useOperatorId();

  const isEnabled = enabled && operatorId != null;
  return useQuery({
    queryKey: operatorsQueryKeys.operatorsClientsAll(operatorId),
    queryFn: () => fetchOperatorsClientsAll(String(operatorId)),
    enabled: isEnabled,
    staleTime: STALE_FIVE_MINUTES,
    select: ({ data }) => data,
  });
}

export function usePostOperatorsClientsMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<ClientSchemaType, AxiosError, ClientSchemaType>({
    mutationKey: operatorsQueryKeys.operatorsClients(operatorId),
    mutationFn: (data) => postOperatorsClients(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate clients
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.operatorsClients(operatorId) });
    },
  });
}

export function usePutOperatorsClientsMutation() {
  const queryClient = useQueryClient();
  const operatorId = useOperatorId();

  return useMutation<ClientSchemaType, AxiosError, ClientSchemaType>({
    mutationKey: operatorsQueryKeys.operatorsClients(operatorId),
    mutationFn: (data) => putOperatorsClient(String(operatorId), data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate clients
      queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.operatorsClients(operatorId) });
    },
  });
}
